'use client';
import Stories from 'react-insta-stories';
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { ChevronRight, MapPin, X } from 'lucide-react';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ShimmerImage from '@/components/ui/ShimmerImage';
import Button from '@/components/ui/Button';
import clsx from 'clsx';
import dayJS from '@/lib/dayjs';
import { motion } from 'framer-motion';
import { FeaturedCompanyListDTO } from '@/shared';
import { FeaturedJobListDTO } from '@/shared/job/job.dto';
import JobSaveButton from '@/features/jobs/components/JobSaveButton';
const WithSeeMore = React.lazy(() =>
  import('react-insta-stories').then((module) => ({
    default: module.WithSeeMore,
  }))
);
const Story = ({
  job,
  company,
}: {
  job: FeaturedJobListDTO;
  company: FeaturedCompanyListDTO;
  action: () => void;
  isPaused: boolean;
}) => {
  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '-100%' }}
      transition={{ duration: 0.5, ease: 'easeInOut' }}
      className="w-full h-full"
      style={{ willChange: 'transform' }}
    >
      <div className="p-12 flex flex-col bg-primary/90 backdrop-blur-sm w-full h-full text-white bg-[url('/images/backgrounds/story-bg.png')]">
        <div className="flex flex-col justify-center items-center gap-2 mt-12">
          <div className="rounded-full w-16 h-16 flex justify-center items-center bg-white overflow-hidden">
            <ShimmerImage
              src={`${company.logo}`}
              alt={`${company.name}`}
              objectID={company.id}
              objectType="company"
              width={64}
              height={64}
            />
          </div>
          <p className="font-medium text-xs">{company.name}</p>
          <span className="text-[10px]">
            {dayJS.unix(Number(job.time)).format('YYYY-MM-DD HH:mm')}
          </span>
        </div>
        <div className="flex flex-col gap-4 text-center mt-10">
          <h1 className="text-xl font-medium">{job.title}</h1>
          <p className="text-xl">{job.salary}</p>
          <div className="flex gap-1 justify-center items-center text-xs">
            <MapPin size={16} /> {job.address}
          </div>
        </div>
        {/*<div className="flex flex-col gap-2 w-full">
          <Button
            variant={'outline'}
            className="border border-white rounded-lg h-10 text-white"
          >
            <Bookmark
              size={20}
              className="text-white shrink-0 absolute left-4"
            />
            Хадгалах
          </Button>
          <Button
            className="bg-white text-primary h-10"
            href={`/job/${job.code}`}
          >
            Дэлгэрэнгүй
            <ChevronRight size={20} className="shrink-0 absolute right-4" />
          </Button>
        </div>*/}
      </div>
    </motion.div>
  );
};

const FeaturedCompaniesSlider = function ({
  companies,
  isOpen,
  onClose,
  initialCompanyId = null,
}: {
  companies: FeaturedCompanyListDTO[];
  isOpen: boolean;
  onClose: () => void;
  initialCompanyId: number | null;
}) {
  const formatCompanies = useCallback((companies: FeaturedCompanyListDTO[]) => {
    return companies
      .filter((company) => company.works && company.works.length > 0)
      .map((company, index) => ({
        index,
        company,
        stories:
          company.works &&
          company.works.map((job) => ({
            content: ({ action, story }) => {
              return (
                <Suspense>
                  <WithSeeMore story={story} action={action}>
                    <Story
                      job={job}
                      company={company}
                      action={action}
                      isPaused={false}
                    />
                  </WithSeeMore>
                </Suspense>
              );
            },
            seeMoreCollapsed: () => (
              <div className="flex flex-col gap-2 w-full p-12">
                <JobSaveButton jobId={job.id} />
                <Button
                  className="bg-white text-primary h-10 hover:bg-white"
                  href={`/job/_${job.code}`}
                >
                  Дэлгэрэнгүй
                  <ChevronRight
                    size={20}
                    className="shrink-0 absolute right-4"
                  />
                </Button>
              </div>
            ),
            seeMore: () => (
              <div
                style={{
                  maxWidth: '100%',
                  height: '100%',
                  padding: 40,
                  background: 'white',
                }}
              >
                {' '}
              </div>
            ),
          })),
      }));
  }, []);

  const formattedStories = formatCompanies(companies);

  const resolvedIndex = useMemo(() => {
    if (initialCompanyId == null) return 0;
    const i = formattedStories.findIndex(
      (s) => s.company.id === initialCompanyId
    );
    return i >= 0 ? i : 0;
  }, [initialCompanyId, formattedStories]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(0);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(resolvedIndex);
    } else {
      setCurrentIndex(0);
    }
  }, [isOpen, resolvedIndex]);

  const handleStoryClick = (index: number) => {
    setCurrentIndex(index);
  };

  const handleAllStoriesEnd = useCallback(() => {
    const nextIndex = currentIndexRef.current + 1;
    if (nextIndex >= formattedStories.length) {
      onClose?.();
      return;
    }
    setCurrentIndex(nextIndex);
  }, [formattedStories.length, onClose]);

  return (
    <Transition show={isOpen}>
      <Dialog onClose={onClose} className="relative z-50">
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80" />
        </TransitionChild>

        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div
            className="fixed inset-0 flex w-screen items-center justify-center p-4 z-10"
          >
            <button
              className="shrink-0 absolute right-4 top-4 rounded-full p-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-white"
              onClick={onClose}
            >
              <X className="size-6" />
            </button>
            <DialogPanel className="max-w-full max-h-[calc(100dvh-48px)] flex flex-col overflow-hidden divide-y rounded-xl">
              <div className="shrink grow overflow-hidden overflow-y-auto relative w-screen flex items-center justify-center"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    onClose();
                  }
                }}
              >
                <div className="flex relative">
                  {formattedStories.map((story, index) => {
                    const isAdjacentToCurrent =
                      Math.abs(index - currentIndex) === 1;

                    let offset;
                    if (index < currentIndex) {
                      const baseOffset = isAdjacentToCurrent ? 680 : 580;
                      const additionalOffset =
                        index < currentIndex - 1
                          ? (currentIndex - index - 1) * 580
                          : 0;
                      offset = -(baseOffset + additionalOffset);
                    } else if (index > currentIndex) {
                      const baseOffset = isAdjacentToCurrent ? 680 : 580;
                      const additionalOffset =
                        index > currentIndex + 1
                          ? (index - currentIndex - 1) * 580
                          : 0;
                      offset = baseOffset + additionalOffset;
                    } else {
                      offset = 0;
                    }

                    return (
                      <div
                        key={story.company.id}
                        className={clsx(
                          'transition-all duration-300 absolute',
                          index === currentIndex && 'relative z-20 scale-100',
                          index !== currentIndex &&
                            'z-10 scale-50 opacity-50 cursor-pointer'
                        )}
                        style={{
                          transform:
                            index === currentIndex
                              ? 'none'
                              : `translateX(${offset}px)`,
                        }}
                        onClick={
                          index !== currentIndex
                            ? () => handleStoryClick(index)
                            : undefined
                        }
                      >
                        {index !== currentIndex ? (
                          <div className="hidden p-12 lg:flex flex-col gap-12 bg-black justify-center items-center w-[360px] h-[576px] text-white">
                            <div className="flex flex-col justify-center items-center gap-2">
                              <div className="rounded-full w-36 h-36 flex justify-center items-center bg-white overflow-hidden">
                                <ShimmerImage
                                  src={`${story.company.logo}`}
                                  alt={story.company.name}
                                  objectID={story.company.id}
                                  objectType="company"
                                  width={144}
                                  height={144}
                                />
                              </div>
                              <p className="font-medium text-xl text-center">
                                {story.company.name}
                              </p>
                              <p className="text-xl text-center">
                                {story.company.works &&
                                  story.company.works.length}{' '}
                                ажлын байр
                              </p>
                            </div>
                          </div>
                        ) : (
                          <Stories
                            key={story.company.id}
                            stories={story.stories ?? []}
                            width={360}
                            height={576}
                            loop={false}
                            isPaused={false}
                            onAllStoriesEnd={handleAllStoriesEnd}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </DialogPanel>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

export default FeaturedCompaniesSlider;
